import { useState } from 'react';

import cn from 'classnames';
import { EMAIL_TEMPLATE_TYPES } from 'constants/email-config';
import { Controller, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { EditableSettings } from 'components/EditableSettings/EditableSettings';
import { EmailTemplatePreviewModal } from 'components/EmailTemplatePreviewModal/EmailTemplatePreviewModal';
import { Button, InputGroup, Select, Textarea, ToggleSwitch } from 'components/FormComponents';

import { useGetEmailTemplatesQuery } from 'api/email-templates-api';

const getCompositeValue = template =>
  template.is_system_default ? `default-${template.id}` : `company-${template.id}`;

const getSelectValue = (fieldValue, templates) => {
  if (fieldValue == null || fieldValue === '') return '';
  if (typeof fieldValue === 'string' && /^(company|default)-\d+$/.test(fieldValue)) {
    return templates.some(t => getCompositeValue(t) === fieldValue) ? fieldValue : '';
  }
  const num = Number(fieldValue);
  if (Number.isNaN(num)) return '';
  const company = templates.find(t => !t.is_system_default && t.id === num);
  if (company) return `company-${company.id}`;
  const override = templates.find(t => !t.is_system_default && t.system_default_id === num);
  return override ? `company-${override.id}` : '';
};

const resolveTemplateForPreview = (fieldValue, templates) => {
  if (fieldValue == null || fieldValue === '') return null;
  if (typeof fieldValue === 'string' && /^company-(\d+)$/.test(fieldValue)) {
    const m = fieldValue.match(/^company-(\d+)$/);
    const id = parseInt(m[1], 10);
    return templates.find(t => !t.is_system_default && Number(t.id) === id) ?? null;
  }
  const want = Number(fieldValue);
  if (Number.isNaN(want)) return null;
  const direct = templates.find(t => !t.is_system_default && Number(t.id) === want);
  if (direct) return direct;
  return templates.find(t => !t.is_system_default && Number(t.system_default_id) === want) ?? null;
};

export const Emails = ({
  brand,
  register,
  control,
  readOnly = false,
  backgroundColor = 'background-secondary',
  sendCandidateEmailPreview = () => {},
  sendRefereeEmailPreview = () => {},
}) => {
  const { t } = useTranslation();

  const [previewState, setPreviewState] = useState({ visible: false, template: null });
  const includeSupportFooterValue = useWatch({ control, name: 'include_support_footer' });
  const showSupportFooterInPreview = includeSupportFooterValue ?? true;

  const emailActiveValue = useWatch({ control, name: 'email_active' });
  const emailCandidateActiveValue = useWatch({ control, name: 'email_candidate_active' });
  const emailRefereeActiveValue = useWatch({ control, name: 'email_referee_active' });
  const smsActiveValue = useWatch({ control, name: 'sms_active' });
  const smsCandidateActiveValue = useWatch({ control, name: 'sms_candidate_active' });
  const smsRefereeActiveValue = useWatch({ control, name: 'sms_referee_active' });

  const { data: candidateRequestTemplates = [] } = useGetEmailTemplatesQuery({
    category: 'candidate',
    type: EMAIL_TEMPLATE_TYPES.CANDIDATE_REQUEST,
  });
  const { data: candidatePortalTemplates = [] } = useGetEmailTemplatesQuery({
    category: 'candidate',
    type: EMAIL_TEMPLATE_TYPES.CANDIDATE_PORTAL_INVITATION,
  });
  const { data: refereeTemplates = [] } = useGetEmailTemplatesQuery({
    category: 'referee',
    type: EMAIL_TEMPLATE_TYPES.NEW_REQUEST_TO_REFEREE,
  });

  const makeTemplateChangeHandler = onChange => compositeValue => {
    onChange(compositeValue && compositeValue !== '' ? compositeValue : null);
  };

  const openTemplatePreview = (fieldValue, templates) => {
    const template = resolveTemplateForPreview(fieldValue, templates);
    if (!template) return;
    setPreviewState({ visible: true, template });
  };

  const closeTemplatePreview = () => setPreviewState({ visible: false, template: null });

  return (
    <div className={cn('u-padding', backgroundColor)}>
      <div className={cn('card', !readOnly ? 'card-with-border u-padding' : '')}>
        <div className="u-margin-bottom">
          <InputGroup>
            <ToggleSwitch
              register={register}
              name="use_email_communication"
              label={t('company.tabs.branding.emails.useEmailCommunication')}
            />
          </InputGroup>

          <InputGroup>
            <ToggleSwitch
              register={register}
              name="use_sms_communication"
              label={t('company.tabs.branding.emails.useSmsCommunication')}
            />
          </InputGroup>

          <InputGroup>
            <ToggleSwitch
              register={register}
              name="include_support_footer"
              label={t('company.tabs.branding.emails.includeSupportFooter')}
            />
          </InputGroup>
        </div>

        <EditableSettings
          title={t('company.tabs.branding.emails.useCustomizedEmails')}
          open={emailActiveValue}
          toggleable
          register={register}
          name="email_active"
          className="mb-6">
          <EditableSettings
            title={t('company.tabs.branding.emails.useCustomizedEmailsForCandidate')}
            open={emailCandidateActiveValue}
            toggleable
            register={register}
            className="mb-6"
            name="email_candidate_active">
            <InputGroup title={t('company.tabs.branding.emails.candidateEmailTemplate')}>
              <Controller
                name="email_candidate_template_id"
                control={control}
                render={({ field }) => (
                  <div className="flex flex-col items-start w-full gap-2">
                    <div className="u-flex-grow-1 u-width-100">
                      <Select
                        value={getSelectValue(field.value, candidateRequestTemplates)}
                        onChange={e => makeTemplateChangeHandler(field.onChange)(e.target.value)}>
                        <Select.Item value="">
                          {t('company.tabs.branding.emails.noTemplate')}
                        </Select.Item>
                        {candidateRequestTemplates
                          .filter(template => !template.is_system_default)
                          .map(template => {
                            const compositeValue = getCompositeValue(template);
                            return (
                              <Select.Item key={compositeValue} value={compositeValue}>
                                {template.name}
                              </Select.Item>
                            );
                          })}
                      </Select>
                    </div>
                    <Button
                      type="link"
                      className="h-fit text-xl underline"
                      disabled={!field.value}
                      onClick={() => openTemplatePreview(field.value, candidateRequestTemplates)}>
                      {t('company.tabs.branding.emails.previewTemplate')}
                    </Button>
                  </div>
                )}
              />
            </InputGroup>

            <InputGroup title={t('company.tabs.branding.emails.candidatePortalEmailTemplate')}>
              <Controller
                name="email_candidate_portal_template_id"
                control={control}
                render={({ field }) => (
                  <div className="flex flex-col items-start w-full gap-2">
                    <div className="u-flex-grow-1 u-width-100">
                      <Select
                        value={getSelectValue(field.value, candidatePortalTemplates)}
                        onChange={e => makeTemplateChangeHandler(field.onChange)(e.target.value)}>
                        <Select.Item value="">
                          {t('company.tabs.branding.emails.noTemplate')}
                        </Select.Item>
                        {candidatePortalTemplates
                          .filter(template => !template.is_system_default)
                          .map(template => {
                            const compositeValue = getCompositeValue(template);
                            return (
                              <Select.Item key={compositeValue} value={compositeValue}>
                                {template.name}
                              </Select.Item>
                            );
                          })}
                      </Select>
                    </div>
                    <Button
                      type="link"
                      className="h-fit text-xl underline"
                      disabled={!field.value}
                      onClick={() => openTemplatePreview(field.value, candidatePortalTemplates)}>
                      {t('company.tabs.branding.emails.previewTemplate')}
                    </Button>
                  </div>
                )}
              />
            </InputGroup>

            <div
              className={cn(
                'u-flex-box',
                'u-width-100',
                'u-flex-justify-end',
                'u-margin-bottom--small',
              )}>
              <Button type="secondary" onClick={sendCandidateEmailPreview}>
                {t('company.tabs.branding.emails.previewCandidateEmail')}
              </Button>
            </div>
          </EditableSettings>

          <EditableSettings
            title={t('company.tabs.branding.emails.useCustomizedEmailsForReferee')}
            open={emailRefereeActiveValue}
            toggleable
            register={register}
            name="email_referee_active">
            <InputGroup title={t('company.tabs.branding.emails.refereeEmailTemplate')}>
              <Controller
                name="email_referee_template_id"
                control={control}
                render={({ field }) => (
                  <div className="flex flex-col items-start w-full gap-2">
                    <div className="u-flex-grow-1 u-width-100">
                      <Select
                        value={getSelectValue(field.value, refereeTemplates)}
                        onChange={e => makeTemplateChangeHandler(field.onChange)(e.target.value)}>
                        <Select.Item value="">
                          {t('company.tabs.branding.emails.noTemplate')}
                        </Select.Item>
                        {refereeTemplates
                          .filter(template => !template.is_system_default)
                          .map(template => {
                            const compositeValue = getCompositeValue(template);
                            return (
                              <Select.Item key={compositeValue} value={compositeValue}>
                                {template.name}
                              </Select.Item>
                            );
                          })}
                      </Select>
                    </div>
                    <Button
                      type="link"
                      className="h-fit text-xl underline"
                      disabled={!field.value}
                      onClick={() => openTemplatePreview(field.value, refereeTemplates)}>
                      {t('company.tabs.branding.emails.previewTemplate')}
                    </Button>
                  </div>
                )}
              />
            </InputGroup>

            <div
              className={cn(
                'u-flex-box',
                'u-width-100',
                'u-flex-justify-end',
                'u-margin-bottom--small',
              )}>
              <Button type="secondary" onClick={sendRefereeEmailPreview}>
                {t('company.tabs.branding.emails.previewRefereeEmail')}
              </Button>
            </div>
          </EditableSettings>
        </EditableSettings>

        <EditableSettings
          title={t('company.tabs.branding.emails.useCustomizedSms')}
          open={smsActiveValue}
          toggleable
          register={register}
          name="sms_active"
          className="u-margin-bottom">
          <EditableSettings
            title={t('company.tabs.branding.emails.useCustomizedSmsForCandidate')}
            className="u-margin-bottom"
            open={smsCandidateActiveValue}
            toggleable
            register={register}
            name="sms_candidate_active">
            <InputGroup title={t('company.tabs.branding.emails.body')}>
              <Textarea
                name="sms_candidate_copy"
                register={register}
                placeholder={t('company.tabs.branding.emails.smsCandidateBodyPlaceholder')}
                inputProps={{ rows: 10 }}
              />
            </InputGroup>
          </EditableSettings>

          <EditableSettings
            title={t('company.tabs.branding.emails.useCustomizedSmsForReferee')}
            open={smsRefereeActiveValue}
            toggleable
            register={register}
            name="sms_referee_active">
            <InputGroup title={t('company.tabs.branding.emails.body')}>
              <Textarea
                name="sms_referee_copy"
                register={register}
                placeholder={t('company.tabs.branding.emails.smsRefereeBodyPlaceholder')}
                inputProps={{ rows: 10 }}
              />
            </InputGroup>
          </EditableSettings>
        </EditableSettings>
      </div>

      <EmailTemplatePreviewModal
        brand={brand}
        template={previewState.template}
        visible={previewState.visible}
        onClose={closeTemplatePreview}
        showSupportFooter={showSupportFooterInPreview}
      />
    </div>
  );
};
